import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ConnectorCard from '../components/ConnectorCard';
import { 
  ArrowRight, 
  Zap, 
  Shield, 
  Clock, 
  Code, 
  Database,
  GitBranch,
  Layers,
  Search,
  Terminal
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [featuredConnectors, setFeaturedConnectors] = useState([]);
  const [stats, setStats] = useState({ connectors: 0, deployments: 0, industries: 0 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data if needed
        await axios.post(`${API}/seed`).catch(() => {});
        
        // Fetch featured connectors and stats
        const [connectorsRes, statsRes] = await Promise.all([
          axios.get(`${API}/connectors?limit=6&sort_by=deployment_count`),
          axios.get(`${API}/connectors/stats`)
        ]);
        setFeaturedConnectors(connectorsRes.data);
        
        setStats({
          connectors: statsRes.data.total_connectors || 0,
          deployments: statsRes.data.total_connectors * 15, // Estimated deployments
          industries: statsRes.data.total_industries || 0
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-glow">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/10 rounded-full blur-[120px]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-4xl">
            <Badge className="mb-6 bg-primary/10 text-primary border-primary/30 px-4 py-1.5">
              <Zap className="w-3.5 h-3.5 mr-1.5" />
              The App Store for AI Agent Integrations
            </Badge>
            
            <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6">
              <span className="text-white">Ship AI integrations</span>
              <br />
              <span className="text-gradient">in minutes, not weeks</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-400 leading-relaxed mb-10 max-w-2xl">
              Pipeyard is a curated marketplace of pre-built MCP servers and connectors. 
              Stop rebuilding the same integrations. Deploy instantly to your AI agents.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/marketplace">
                <Button 
                  size="lg" 
                  className="glow-primary bg-primary hover:bg-primary/90 text-white font-medium px-8 py-6 text-lg group"
                  data-testid="explore-marketplace-btn"
                >
                  Explore Marketplace
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/sandbox">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-slate-700 text-slate-300 hover:bg-secondary hover:text-white px-8 py-6 text-lg"
                  data-testid="try-sandbox-btn"
                >
                  <Terminal className="w-5 h-5 mr-2" />
                  Try Sandbox
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8 mt-16 pt-8 border-t border-white/5">
              <div className="stat-number" style={{ animationDelay: '0ms' }}>
                <div className="text-3xl font-heading font-bold text-white">{stats.connectors}+</div>
                <div className="text-sm text-slate-500">Connectors</div>
              </div>
              <div className="stat-number" style={{ animationDelay: '100ms' }}>
                <div className="text-3xl font-heading font-bold text-white">{stats.deployments.toLocaleString()}</div>
                <div className="text-sm text-slate-500">Deployments</div>
              </div>
              <div className="stat-number" style={{ animationDelay: '200ms' }}>
                <div className="text-3xl font-heading font-bold text-white">{stats.industries}</div>
                <div className="text-sm text-slate-500">Industries</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-secondary text-slate-300 border-slate-700">
              How It Works
            </Badge>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-white">
              Three steps to connected agents
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Search,
                title: 'Browse & Search',
                description: 'Find integrations by industry, software platform, or use case. Search our curated directory of MCP servers.',
              },
              {
                icon: Terminal,
                title: 'Test in Sandbox',
                description: 'Try connectors in an isolated environment. Send prompts and see real API responses before deploying.',
              },
              {
                icon: Zap,
                title: 'Deploy Instantly',
                description: 'One-click deployment via Docker, npm, pip, or API. Get your integration running in under a minute.',
              },
            ].map((step, index) => (
              <div 
                key={index}
                className="relative bg-gradient-to-br from-slate-900 to-slate-950 border border-white/5 rounded-2xl p-8 hover:bg-slate-900/80 transition-colors group"
              >
                <div className="absolute -top-4 -left-4 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-white">
                  {index + 1}
                </div>
                <step.icon className="w-10 h-10 text-primary mb-6 group-hover:scale-110 transition-transform" />
                <h3 className="font-heading text-xl font-semibold text-white mb-3">{step.title}</h3>
                <p className="text-slate-400 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Connectors */}
      <section className="py-24 px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
                <Shield className="w-3.5 h-3.5 mr-1.5" />
                Verified Connectors
              </Badge>
              <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-white">
                Popular integrations
              </h2>
            </div>
            <Link to="/marketplace" className="hidden sm:block">
              <Button variant="ghost" className="text-slate-400 hover:text-white group">
                View all
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredConnectors.map((connector) => (
              <ConnectorCard key={connector.id} connector={connector} />
            ))}
          </div>
          
          <div className="mt-8 text-center sm:hidden">
            <Link to="/marketplace">
              <Button variant="outline" className="border-slate-700">
                View all connectors
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">
              Built for developers
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Everything you need to connect AI agents to any software system
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Code, title: 'MCP Standard', desc: 'Universal connector specification for any AI agent' },
              { icon: Shield, title: 'Verified Quality', desc: 'Every connector tested and documented' },
              { icon: Clock, title: 'Instant Deploy', desc: 'Docker, npm, pip, or API key in seconds' },
              { icon: Database, title: 'Usage Analytics', desc: 'Track deployments and API calls' },
              { icon: GitBranch, title: 'Version Control', desc: 'Semantic versioning for all connectors' },
              { icon: Layers, title: 'Multi-Industry', desc: 'Construction, CRM, Finance, Healthcare & more' },
              { icon: Terminal, title: 'Sandbox Testing', desc: 'Test before you deploy' },
              { icon: Zap, title: 'Real-time', desc: 'Live connector status and health' },
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-card border border-white/5 rounded-xl p-6 hover:border-white/10 transition-colors"
              >
                <feature.icon className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-heading font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-white mb-6">
            Ready to supercharge your AI agents?
          </h2>
          <p className="text-lg text-slate-400 mb-10">
            Join developers who ship integrations in minutes instead of weeks.
          </p>
          <Link to="/marketplace">
            <Button 
              size="lg" 
              className="glow-primary bg-primary hover:bg-primary/90 text-white font-medium px-10 py-6 text-lg"
              data-testid="cta-get-started-btn"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-lg text-white">Pipeyard</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
              <Link to="/marketplace" className="hover:text-white transition-colors">Marketplace</Link>
              <Link to="/templates" className="hover:text-white transition-colors">Templates</Link>
              <Link to="/requests" className="hover:text-white transition-colors">Requests</Link>
              <Link to="/community" className="hover:text-white transition-colors">Community</Link>
              <Link to="/health" className="hover:text-white transition-colors">Status</Link>
            </div>
            <p className="text-sm text-slate-600">100% Free • Open for all</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
